// Skills B (owner 2026-11-02 'Make them all'): shared render building blocks for Snare Constellation, Wayward Comet, Binding
// Rings and Gravity Grip. Render-only, pooled, no per-frame allocations:
//  - Cards: camera-facing instanced quads (crisp cel orb cores, soft additive halos, four-point glints) with a per-instance
//    (alpha, heat) attribute; the look is the material's
//  - Ribbons: camera-facing triangle strips through a list of points (trails, threads, tethers) with a hard cel core and a
//    coloured sheath, width and alpha eased from the tail to the head
//  - Decals: flat instanced quads lying on the ground (rings, sigils), procedural TSL patterns, per-instance (alpha, a, b, c)
import * as THREE from 'three/webgpu';
import { abs, attribute, float, instancedBufferAttribute, length, max, mix, smoothstep, uv, vec2, vec3 } from 'three/tsl';
import { uploadPrefix } from './uploadTrim';

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion(), _z = new THREE.Vector3(0, 0, 1);
const _ca = new THREE.Color(), _cb = new THREE.Color(), _cc = new THREE.Color();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

/** Camera-facing instanced quads. `look(a)` builds the material from the per-instance vec2 node a = (alpha, heat). */
export class Cards {
  readonly mesh: THREE.InstancedMesh;
  private attr: THREE.InstancedBufferAttribute; private n = 0;
  constructor(scene: THREE.Scene, readonly cap: number, look: (a: N) => THREE.MeshBasicNodeMaterial, renderOrder = 5) {
    this.attr = new THREE.InstancedBufferAttribute(new Float32Array(cap * 2), 2); this.attr.setUsage(THREE.DynamicDrawUsage);
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), look(instancedBufferAttribute(this.attr)), cap);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = renderOrder;
    scene.add(this.mesh);
  }
  begin(): void { this.n = 0; }
  /** one card at (x, y, z), `size` m across, oriented by q (the camera quaternion: facing the camera; see pushRolled);
   *  tint multiplies the material colour */
  push(x: number, y: number, z: number, size: number, q: THREE.Quaternion, alpha: number, heat = 0, r = 1, g = 1, b = 1): void {
    if (this.n >= this.cap || alpha <= 0.003 || size <= 0.001) return;
    const i = this.n++;
    this.mesh.setMatrixAt(i, _m.compose(_p.set(x, y, z), q, _s.set(size, size, size)));
    const A = this.attr.array as Float32Array; A[i * 2] = alpha; A[i * 2 + 1] = heat;
    const C = this.mesh.instanceColor!.array as Float32Array; C[i * 3] = r; C[i * 3 + 1] = g; C[i * 3 + 2] = b;
  }
  end(): void {
    this.mesh.count = this.n;
    if (!this.n) return;
    this.mesh.instanceMatrix.needsUpdate = true; this.mesh.instanceColor!.needsUpdate = true; this.attr.needsUpdate = true;
  }
  clear(): void { this.n = 0; this.mesh.count = 0; }
}
const _roll = new THREE.Quaternion(), _q2 = new THREE.Quaternion();
/** a camera-facing card rolled by `rot` about the view axis (glints that spin) */
export function pushRolled(c: Cards, x: number, y: number, z: number, size: number, camQ: THREE.Quaternion, alpha: number, heat: number, rot: number, r = 1, g = 1, b = 1): void {
  _roll.setFromAxisAngle(_z, rot);
  c.push(x, y, z, size, _q2.copy(camQ).multiply(_roll), alpha, heat, r, g, b);
}

/** A crisp cel orb: white-hot core (grows with heat), saturated body, thin darker rim, hard silhouette. Normal blending so it
 *  keeps its colour over bright grass; the core goes above 1 so bloom picks it up. */
export function orbLook(core: number, body: number, rim: number, coreK = 1.7): (a: N) => THREE.MeshBasicNodeMaterial {
  return (a) => {
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
    const d = length(uv().sub(vec2(0.5, 0.5))).mul(2);
    const cR = mix(float(0.34), float(0.62), a.y);
    _ca.setHex(core); _cb.setHex(body); _cc.setHex(rim);
    const c1 = mix(vec3(_cc.r, _cc.g, _cc.b), vec3(_cb.r, _cb.g, _cb.b).mul(1.15), smoothstep(0.97, 0.86, d));
    m.colorNode = mix(c1, vec3(_ca.r, _ca.g, _ca.b).mul(coreK), smoothstep(cR, cR.mul(0.72), d));
    m.opacityNode = smoothstep(1.0, 0.93, d).mul(a.x);
    return m;
  };
}
/** A soft additive glow (quadratic falloff); alpha scales it */
export function haloLook(hex: number, k = 0.7): (a: N) => THREE.MeshBasicNodeMaterial {
  return (a) => {
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false, blending: THREE.AdditiveBlending });
    const d = length(uv().sub(vec2(0.5, 0.5))).mul(2), f = float(1).sub(d).max(0);
    _ca.setHex(hex);
    m.colorNode = vec3(_ca.r, _ca.g, _ca.b).mul(f.mul(f).mul(k).mul(a.x));
    return m;
  };
}
/** A four-point star glint (thin crossed spikes and a small hot centre), additive */
export function glintLook(hex: number, k = 1.2): (a: N) => THREE.MeshBasicNodeMaterial {
  return (a) => {
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false, blending: THREE.AdditiveBlending });
    const p = uv().sub(vec2(0.5, 0.5)).mul(2), ax = abs(p.x), ay = abs(p.y);
    const armH = smoothstep(float(0.09), float(0), ay.mul(float(1).add(ax.mul(6)))).mul(smoothstep(1, 0.2, ax));
    const armV = smoothstep(float(0.09), float(0), ax.mul(float(1).add(ay.mul(6)))).mul(smoothstep(1, 0.2, ay));
    const c = smoothstep(0.25, 0, length(p));
    _ca.setHex(hex);
    m.colorNode = mix(vec3(_ca.r, _ca.g, _ca.b), vec3(1, 1, 1), c).mul(max(armH, armV).add(c.mul(1.5)).mul(k).mul(a.x));
    return m;
  };
}

/** Camera-facing triangle strips through point lists: hard cel core + coloured sheath. One pooled dynamic mesh. */
export class Ribbons {
  readonly mesh: THREE.Mesh;
  private geo = new THREE.BufferGeometry();
  private pos: Float32Array; private edge: Float32Array; private core: Float32Array;
  private n = 0;
  constructor(scene: THREE.Scene, readonly maxStrips: number, readonly maxPts: number, renderOrder = 5, additive = false) {
    const vPer = maxPts * 2, nV = maxStrips * vPer, seg = maxPts - 1;
    this.pos = new Float32Array(nV * 3); this.edge = new Float32Array(nV * 4); this.core = new Float32Array(nV * 4);
    const uvs = new Float32Array(nV * 2), idx = new Uint32Array(maxStrips * seg * 6);
    for (let s = 0; s < maxStrips; s++) {
      for (let i = 0; i < maxPts; i++) { const v = s * vPer + i * 2; uvs[v * 2] = i / seg; uvs[v * 2 + 1] = 0; uvs[v * 2 + 2] = i / seg; uvs[v * 2 + 3] = 1; }
      for (let i = 0; i < seg; i++) { const v = s * vPer + i * 2, o = (s * seg + i) * 6; idx[o] = v; idx[o + 1] = v + 1; idx[o + 2] = v + 2; idx[o + 3] = v + 2; idx[o + 4] = v + 1; idx[o + 5] = v + 3; }
    }
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('kedge', new THREE.BufferAttribute(this.edge, 4).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('kcore', new THREE.BufferAttribute(this.core, 4).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    this.geo.setIndex(new THREE.BufferAttribute(idx, 1));
    this.geo.setDrawRange(0, 0);
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false, blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending });
    const e = attribute('kedge', 'vec4') as N, c = attribute('kcore', 'vec4') as N;
    const across = abs(uv().y.sub(0.5)).mul(2);
    m.colorNode = mix(vec3(e.x, e.y, e.z), vec3(c.x, c.y, c.z), smoothstep(c.w, c.w.mul(0.62), across));
    m.opacityNode = smoothstep(1.0, 0.86, across).mul(e.w);
    this.mesh = new THREE.Mesh(this.geo, m); this.mesh.frustumCulled = false; this.mesh.renderOrder = renderOrder;
    scene.add(this.mesh);
  }
  begin(): void { this.n = 0; }
  /** A strip through n points (x, y, z triples; index 0 = tail, n-1 = head): width and alpha go from (w0, a0) at the tail to
   *  (w1, a1) at the head (alpha with an ease `ap`); edge/core colours (may be > 1 for bloom), core half-width share coreW. */
  strip(pts: ArrayLike<number>, n: number, w0: number, w1: number, a0: number, a1: number, edge: THREE.Color, core: THREE.Color, coreW: number, cam: THREE.Vector3, ap = 1): void {
    if (this.n >= this.maxStrips || n < 2) return;
    const M = this.maxPts, base = this.n * M * 2, P = this.pos, E = this.edge, C = this.core;
    n = Math.min(n, M);
    let lx = 0, ly = 0, lz = 0;
    for (let i = 0; i < M; i++) {
      const j = Math.min(i, n - 1), u = n > 1 ? j / (n - 1) : 1;
      const x = pts[j * 3], y = pts[j * 3 + 1], z = pts[j * 3 + 2];
      let w = 0, a = 0;
      if (i < n) {
        const ja = Math.max(0, j - 1), jb = Math.min(n - 1, j + 1);
        const tx = pts[jb * 3] - pts[ja * 3], ty = pts[jb * 3 + 1] - pts[ja * 3 + 1], tz = pts[jb * 3 + 2] - pts[ja * 3 + 2];
        const vx = cam.x - x, vy = cam.y - y, vz = cam.z - z;
        let sx = ty * vz - tz * vy, sy = tz * vx - tx * vz, sz = tx * vy - ty * vx;
        const sl = Math.hypot(sx, sy, sz);
        if (sl > 1e-6) { w = (w0 + (w1 - w0) * u) * 0.5; sx = sx / sl * w; sy = sy / sl * w; sz = sz / sl * w; lx = sx; ly = sy; lz = sz; } else { sx = lx; sy = ly; sz = lz; }
        a = a0 + (a1 - a0) * Math.pow(u, ap);
        const v = (base + i * 2) * 3;
        P[v] = x - sx; P[v + 1] = y - sy; P[v + 2] = z - sz; P[v + 3] = x + sx; P[v + 4] = y + sy; P[v + 5] = z + sz;
      } else { const v = (base + i * 2) * 3; P[v] = P[v + 3] = x; P[v + 1] = P[v + 4] = y; P[v + 2] = P[v + 5] = z; }
      for (let k = 0; k < 2; k++) {
        const o = (base + i * 2 + k) * 4;
        E[o] = edge.r; E[o + 1] = edge.g; E[o + 2] = edge.b; E[o + 3] = a;
        C[o] = core.r; C[o + 1] = core.g; C[o + 2] = core.b; C[o + 3] = coreW;
      }
    }
    this.n++;
  }
  end(): void {
    this.geo.setDrawRange(0, this.n * (this.maxPts - 1) * 6);
    // PERF-STRESS: upload only the strips in use
    uploadPrefix(this.n * this.maxPts * 2, this.geo.attributes.position as THREE.BufferAttribute, this.geo.attributes.kedge as THREE.BufferAttribute, this.geo.attributes.kcore as THREE.BufferAttribute);
  }
  clear(): void { this.n = 0; this.geo.setDrawRange(0, 0); }
}

/** Flat instanced quads lying on the ground (y up). `look(a)` builds the material from the per-instance vec4 a. */
export class Decals {
  readonly mesh: THREE.InstancedMesh;
  private attr: THREE.InstancedBufferAttribute; private n = 0;
  constructor(scene: THREE.Scene, readonly cap: number, look: (a: N) => THREE.MeshBasicNodeMaterial, renderOrder = 1) {
    this.attr = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4); this.attr.setUsage(THREE.DynamicDrawUsage);
    const mat = look(instancedBufferAttribute(this.attr));
    mat.polygonOffset = true; mat.polygonOffsetFactor = -3; mat.polygonOffsetUnits = -4;
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2), mat, cap);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = renderOrder;
    scene.add(this.mesh);
  }
  begin(): void { this.n = 0; }
  /** a decal `size` m across centred at (x, y, z), turned by yaw */
  push(x: number, y: number, z: number, size: number, yaw: number, a0: number, a1 = 0, a2 = 0, a3 = 0): void {
    if (this.n >= this.cap || a0 <= 0.003) return;
    const i = this.n++;
    _q.setFromAxisAngle(_up, yaw);
    this.mesh.setMatrixAt(i, _m.compose(_p.set(x, y, z), _q, _s.set(size, 1, size)));
    const A = this.attr.array as Float32Array; A[i * 4] = a0; A[i * 4 + 1] = a1; A[i * 4 + 2] = a2; A[i * 4 + 3] = a3;
  }
  end(): void { this.mesh.count = this.n; if (this.n) { this.mesh.instanceMatrix.needsUpdate = true; this.attr.needsUpdate = true; } }
  clear(): void { this.n = 0; this.mesh.count = 0; }
}
const _up = new THREE.Vector3(0, 1, 0);

/** ease helpers */
export const easeOut = (u: number): number => 1 - (1 - u) * (1 - u) * (1 - u);
export const clamp01 = (u: number): number => (u < 0 ? 0 : u > 1 ? 1 : u);
/** a snappy pop-in scale: 0 → overshoot 1.25 → settles at 1 over `dur` s */
export const popIn = (t: number, dur: number): number => { const u = clamp01(t / dur); return u >= 1 ? 1 : u < 0.6 ? (u / 0.6) * 1.25 : 1.25 - 0.25 * ((u - 0.6) / 0.4); };
/** shared scratch colour builder: hex × k */
export const col = (out: THREE.Color, hex: number, k = 1): THREE.Color => out.setHex(hex).multiplyScalar(k);
